import { Card, CardContent, CardHeader, FormControl, FormControlLabel, FormHelperText, Grid2, IconButton, Stack, Switch } from "@mui/material"
import DashboardLayout from "../../../layoutcontainers/DashboardLayout"
import { ArrowBack } from "@mui/icons-material"
import MaterialTypography from "../../../components/MaterialTypography"
import { useNavigate, useSearchParams } from "react-router-dom"
import MaterialBox from "../../../components/MaterialBox"
import { useCoverageFormHandling } from "./components/CoverageStateHandle"
import MaterialSelect from "../../../components/MasterSelect"
import { useEffect, useState } from "react"
import { fetchData, fetchDataById, postData, putData } from "../../../services"
import MaterialInput from "../../../components/MaterialInput"
import MaterialButton from "../../../components/MaterialButton"
import { useSnackbar } from "../../../context/popup"
import DropDownConfig from "../../Utils/DropDownConfig"
import { useLoading } from "../../../components/Loading"

const InsurerCoverageForm = () => {

    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { showSnackbar } = useSnackbar();
    const { setCircleLoading } = useLoading();
    const coverageId = searchParams.get("id");
    const pageType = searchParams.get("pageType");
    const pageMode = pageType === "view" ? "view" : pageType === "edit" 
    ? "edit" : "add";
    const [generalInsurerOptions, setGeneralInsurerOptions] = useState([]);
    const [generalCoverageOptions, setGeneralCoverageOptions] = useState([]);
    const [generalCoverageTypeOptions, setGeneralCoverageTypeOptions] = useState([]);
    const [productOptions, setProductOptions] = useState([]);
    const [productTypeOptions, setProductTypeOptions] = useState([]);
    const { formData, setFormData, handleChange, checkAllFields } = useCoverageFormHandling();  
    
    useEffect(() => {
        if((pageMode === "add") || (!coverageId)){
            return
        }
        const fetchCoverageDataById = async (coverageId) => {
            try {
            setCircleLoading(true);
            const data = await fetchDataById(`/reference/insurer/coverage/${coverageId}`);
            const responseData = data?.data?.coverage;
            
            if(data?.success){
                setFormData(prev => ({
                ...prev,
                gInsurer: {value: responseData?.gInsurerId, label: responseData?.gInsurerName},
                gAddon: {value: responseData?.gAddonId, label: responseData?.gAddonId},
                name: responseData.name || "",
                code: responseData.code || "",
                gProduct: {value: responseData?.gProductId, label: responseData?.gProductName},
                gPolicyType: {value: responseData?.gPolicyTypeId, label: responseData?.gPolicyTypeName},
                isActive: responseData?.isActive,
                // reset status fields
                nameStatus: responseData.name ? "success" : "unchecked",
                codeStatus: responseData.code ? "success" : "unchecked",
                gInsurerStatus: responseData?.gInsurerId ? "success" : "unchecked",
                gAddonStatus: responseData?.gAddonId ? "success" : "unchecked",
                gProductStatus: responseData?.gProductId  ? "success" : "unchecked",
                gPolicyTypeStatus: responseData?.gPolicyTypeId ? "success" : "unchecked"
                }));
            }
            else{
                //throw error message
            }
            } catch (error) {
            console.error('Error fetching data by ID:', error);
            showSnackbar(error?.response?.data?.message,"error");
            }finally{
                setCircleLoading(false);
            }
        };
        fetchCoverageDataById(coverageId);
    },[pageMode]);

    useEffect(() => {
        if(pageMode === "view"){
            return
        }
        fetchAllDropdowns();
    }, []);

    //all dropdown api function
    const fetchAllDropdowns = async () => {
        setCircleLoading(true);

        try {
            const [
            insurerResponse,
            coverageResponse,
            coverageTypeResponse,
            productResponse,
            productTypeResponse,
            ] = await Promise.all([
            fetchData(`/reference/general/insurer-master?dropdown=true&isActive=true`),
            fetchData(`/reference/general/coverage?dropdown=true&isActive=true`),
            fetchData(`/reference/general/key-value-store?dropdown=true&code=${DropDownConfig?.generalCoverageType}`),
            fetchData(`/reference/general/key-value-store?dropdown=true&code=${DropDownConfig?.generalProduct}`),
            fetchData(`/reference/general/key-value-store?dropdown=true&code=${DropDownConfig?.generalPolicyType}`),
            ]);

            const formattedInsurers = insurerResponse?.data?.items?.map((item) => ({
                value: item?.id,
                label: item?.companyName,
            })) || [];

            const formattedCoverages = coverageResponse?.data?.items?.map((item) => ({
                value: item?.id,
                label: item?.name,
            })) || [];

            const formattedCoverageTypes = coverageTypeResponse?.data?.items?.map((item) => ({
                value: item?.id,
                label: item?.keyName,
            })) || [];

            const formattedProducts = productResponse?.data?.items?.map((item) => ({
                label: item?.keyName,
                value: item?.id,
            })) || [];

            const formattedProductTypes = productTypeResponse?.data?.items?.map((item) => ({
                label: item?.keyName,
                value: item?.id,
            })) || [];
            
            setGeneralInsurerOptions(formattedInsurers);
            setGeneralCoverageOptions(formattedCoverages);
            setGeneralCoverageTypeOptions(formattedCoverageTypes);
            setProductOptions(formattedProducts);
            setProductTypeOptions(formattedProductTypes);

        } catch (error) {
            console.error("Error fetching dropdown data:", error);
            showSnackbar("Error Fetching Dropdown Data","warning");
        } finally {
            setCircleLoading(false);
        }
    };

    //both post and put function
    const handleSubmitCoverage = async () => {
        const payload = {
            gInsurerId: formData?.gInsurer?.value,
            gCoverageId: formData?.gCoverage?.value,
            gCoverageTypeId: formData?.gCoverageType?.value,
            name: formData?.name,
            code: formData?.code,
            gProductId: formData?.gProduct?.value,
            gPolicyTypeId: formData?.gPolicyType?.value,
            isActive: formData?.isActive,
        };
        //Update
        if(coverageId){
            try {
                setCircleLoading(true);
                const response = await putData(`/reference/insurer/coverage/${coverageId}`, payload);
                const responseMessage = response?.message;
                if(response?.success){
                //success message
                    showSnackbar(responseMessage,"success");
                    navigate('/insurer-master/insurer-coverage');          
                }
                else{
                    //error message
                    showSnackbar(responseMessage,"error")
                }
            } catch (error) {
                console.error('Error updating data:', error);
                showSnackbar("Error Updating","error");
            }finally{
                setCircleLoading(false);
            }
        }
        else{      
        //Submit
        try {
            setCircleLoading(true);
            const response = await postData('/reference/insurer/coverage', payload);
            const responseMessage = response?.message;
            if(response?.success){
                //success message
                showSnackbar(responseMessage,"success");
                navigate('/insurer-master/insurer-coverage');          
            }
            else{
                //error message
                showSnackbar(responseMessage,"error");
            }
        } catch (error) {
            console.error('Error posting data:', error);
            //catch error message
            showSnackbar("Error posting","error");
        }finally{
            setCircleLoading(false);
        }
        }
    };

  return (
    <DashboardLayout>
        <Card sx={{ minHeight: 500 }}>
            <CardHeader
            title={(
                <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                spacing={2}
                >
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <IconButton onClick={() => navigate("/insurer-master/insurer-coverage")}>
                            <ArrowBack />
                        </IconButton>
                        <Stack direction="column" spacing={0}>
                        <MaterialTypography variant="h5" fontWeight="medium">
                            Coverage Details
                        </MaterialTypography>
                        <MaterialTypography variant="caption" color="text">
                            Enter New Coverage Details to Insurer.
                        </MaterialTypography>
                        </Stack>
                    </Stack>
                </Stack>
            )}
            />
            <CardContent>
                <Grid2 container spacing={2}>
                    <Grid2 size={{lg:6, md:6, sm: 12, xs: 12 }}>
                        <MaterialBox>
                            <MaterialTypography
                            component="label"
                            variant="caption"
                            fontWeight="bold">
                                General Insurer
                            </MaterialTypography>
                        </MaterialBox>
                        <MaterialSelect
                        name="gInsurer"
                        value={formData.gInsurer}
                        onChange={handleChange}
                        options={generalInsurerOptions}
                        success={formData.gInsurerStatus === "success"}
                        error={formData.gInsurerStatus === "error"}
                        isDisabled={pageMode === "view"}
                        />
                    </Grid2>
                    <Grid2 size={{lg:6, md:6, sm: 12, xs: 12 }}>
                        <MaterialBox>
                            <MaterialTypography
                            component="label"
                            variant="caption"
                            fontWeight="bold">
                                General Coverage
                            </MaterialTypography>
                        </MaterialBox>
                        <MaterialSelect
                        name="gCoverage"
                        value={formData.gCoverage}
                        onChange={handleChange}
                        options={generalCoverageOptions}
                        success={formData.gCoverageStatus === "success"}
                        error={formData.gCoverageStatus === "error"}
                        isDisabled={pageMode === "view"}
                        />
                    </Grid2>
                    <Grid2 size={{lg:6, md:6, sm: 12, xs: 12 }}>
                        <MaterialBox>
                            <MaterialTypography
                            component="label"
                            variant="caption"
                            fontWeight="bold">
                                General Coverage Type
                            </MaterialTypography>
                        </MaterialBox>
                        <MaterialSelect
                        name="gCoverageType"
                        value={formData.gCoverageType}
                        onChange={handleChange}
                        options={generalCoverageTypeOptions}
                        success={formData.gCoverageTypeStatus === "success"}
                        error={formData.gCoverageTypeStatus === "error"}
                        isDisabled={pageMode === "view"}
                        />
                    </Grid2>
                    <Grid2 size={{lg:6, md:6, sm: 12, xs: 12 }}>
                        <MaterialBox>
                            <MaterialTypography
                            component="label"
                            variant="caption"
                            fontWeight="bold">
                                Coverage Name
                            </MaterialTypography>
                        </MaterialBox>
                        <MaterialInput
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        success={formData.nameStatus === "success"}
                        error={formData.nameStatus === "error"}
                        disabled={pageMode === "view"}
                        />
                        {formData.nameStatus === "error" && (
                            <FormControl error>
                                <FormHelperText>
                                Coverage Name should be a minimum 1 character & maximum 100
                                characters long.
                                </FormHelperText>
                            </FormControl>
                        )}
                    </Grid2>
                    <Grid2 size={{lg:6, md:6, sm: 12, xs: 12 }}>
                        <MaterialBox>
                            <MaterialTypography
                            component="label"
                            variant="caption"
                            fontWeight="bold">
                                Coverage Code
                            </MaterialTypography>
                        </MaterialBox>
                        <MaterialInput
                        name="code"
                        value={formData.code}
                        onChange={handleChange}
                        success={formData.codeStatus === "success"}
                        error={formData.codeStatus === "error"}
                        disabled={pageMode === "view"}
                        />
                         {formData.codeStatus === "error" && (
                            <FormControl error>
                                <FormHelperText>
                                Coverage Code should be a minimum 1 character & maximum 20
                                characters long.
                                </FormHelperText>
                            </FormControl>
                        )}
                    </Grid2>
                    <Grid2 size={{lg:6, md:6, sm: 12, xs: 12 }}>
                        <MaterialBox>
                            <MaterialTypography
                            component="label"
                            variant="caption"
                            fontWeight="bold">
                                General Product
                            </MaterialTypography>
                        </MaterialBox>
                        <MaterialSelect
                        name="gProduct"
                        value={formData.gProduct}
                        onChange={handleChange}
                        options={productOptions}
                        success={formData.gProductStatus === "success"}
                        error={formData.gProductStatus === "error"}
                        isDisabled={pageMode === "view"}
                        />
                    </Grid2>
                    <Grid2 size={{lg:6, md:6, sm: 12, xs: 12 }}>
                        <MaterialBox>
                            <MaterialTypography
                            component="label"
                            variant="caption"
                            fontWeight="bold">
                                General Policy Type
                            </MaterialTypography>
                        </MaterialBox>
                        <MaterialSelect
                        name="gPolicyType"
                        value={formData.gPolicyType}
                        onChange={handleChange}
                        options={productTypeOptions}
                        success={formData.gPolicyTypeStatus === "success"}
                        error={formData.gPolicyTypeStatus === "error"}
                        isDisabled={pageMode === "view"}
                        />
                    </Grid2>
                    <Grid2 size={{lg:6, md:6, sm: 12, xs: 12 }}>
                        <MaterialBox>
                            <MaterialTypography
                            component="label"
                            variant="caption"
                            fontWeight="bold">
                                Actve
                            </MaterialTypography>
                        </MaterialBox>
                        <FormControlLabel
                        control={
                            <Switch
                                checked={formData.isActive}
                                disabled={pageMode === "view"}
                                onChange={(e) =>
                                    handleChange({
                                        target: {
                                            name: "isActive",
                                            value: e.target.checked,
                                        },
                                    })
                                }
                                color="primary"
                            />
                        }
                        label={
                            <MaterialTypography variant="caption" fontWeight="bold">
                                {formData?.isActive ? "Yes" : "No"}
                            </MaterialTypography>
                        }
                        sx={{ marginLeft: 0 }}
                        />
                    </Grid2>
                </Grid2>
            </CardContent>
            <MaterialBox
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            >
                {pageMode !== "view" && (
                <>
                    <MaterialButton
                    sx={{ m: 2 }}
                    variant="outlined"
                    color="dark"
                    onClick={() => navigate("/insurer-master/insurer-coverage")}
                    >
                    Cancel
                    </MaterialButton>
                    <MaterialButton
                    sx={{ m: 2 }}
                    variant="gradient"
                    color="dark"
                    disabled={!checkAllFields()}
                    onClick={handleSubmitCoverage}
                    >
                    {pageMode === "edit" ? "Update" : "Submit"}
                    </MaterialButton>
                </>
                )}
            </MaterialBox>
        </Card>
    </DashboardLayout>
  )
}

export default InsurerCoverageForm